import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Search as SearchIcon, Flame } from "lucide-react";

const severityColor = (s) => {
  const map = {
    critical: "bg-rose-100 text-rose-800",
    severe: "bg-rose-100 text-rose-800",
    high: "bg-amber-100 text-amber-800",
    moderate: "bg-amber-100 text-amber-800",
    mild: "bg-slate-100 text-slate-700",
    low: "bg-slate-100 text-slate-700",
  };
  return map[(s || "").toLowerCase()] || "bg-slate-100 text-slate-700";
};

export default function Protocols() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [q, setQ] = useState("");
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const [total, setTotal] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    if (emergencyOnly) params.emergency = true;
    const { data } = await api.get("/protocols", { params });
    setItems(data);
  }, [category, q, emergencyOnly]);

  useEffect(() => {
    api.get("/protocols/categories").then((r) => setCategories(r.data));
    api.get("/stats/counts").then((r) => setTotal(r.data.protocols)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="protocols-page">
      <div>
        <div className="text-xs tracking-widest uppercase text-slate-500 font-semibold">Clinical Reference</div>
        <div className="flex items-center justify-between flex-wrap gap-2 mt-1">
          <h1 className="font-heading text-3xl font-bold text-[#111827]">Clinical Protocols</h1>
          <Badge className="bg-slate-100 text-slate-700 border-0">{items.length} of {total ?? items.length}</Badge>
        </div>
        <p className="text-slate-600 text-sm mt-1">SA-specific stepwise protocols from NDoH STG/EML, SAMF, EMSSA, SEMDSA, Stroke SA & more.</p>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title, definition, presenting complaint..." className="pl-9 h-11 bg-white" data-testid="protocols-search" />
        </div>
        <button
          onClick={() => setEmergencyOnly(!emergencyOnly)}
          className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap flex items-center gap-1.5 ${
            emergencyOnly ? "bg-rose-600 text-white" : "bg-white border border-slate-200 text-slate-700"
          }`}
          data-testid="toggle-emergency"
        >
          <Flame className="w-4 h-4" /> Emergency only
        </button>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-1">
        <button onClick={() => setCategory("")} className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
          !category ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"
        }`} data-testid="cat-all">All</button>
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c === category ? "" : c)}
            className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap capitalize ${
              category === c ? "bg-[#0B5FFF] text-white" : "bg-white border border-slate-200 text-slate-700"
            }`}
            data-testid={`cat-${c}`}
          >
            {c.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((p) => (
          <Link key={p.protocol_id} to={`/app/protocols/${p.protocol_id}`} data-testid={`protocol-card-${p.protocol_id}`}>
            <Card className="border-slate-200 hover:border-[#0B5FFF]/40 hover:shadow-sm h-full">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {p.is_emergency && <Badge className="bg-rose-600 text-white border-0"><Flame className="w-3 h-3 mr-0.5" /> Emergency</Badge>}
                  {p.severity && <Badge className={`${severityColor(p.severity)} border-0 capitalize text-[10px]`}>{p.severity}</Badge>}
                  <span className="text-xs text-slate-500 capitalize">{(p.category || "").replace(/_/g, " ")}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg text-[#111827] leading-snug">{p.title}</h3>
                {p.definition && <p className="text-sm text-slate-600 mt-2 line-clamp-3">{p.definition}</p>}
                {Array.isArray(p.red_flags) && p.red_flags.length > 0 && (
                  <div className="mt-3 text-xs text-rose-700 flex items-start gap-1">
                    <span className="font-semibold">Red flags:</span> <span className="line-clamp-1">{p.red_flags.slice(0, 2).join(" · ")}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
      {items.length === 0 && <div className="text-center py-20 text-slate-500 text-sm">No protocols found.</div>}
    </div>
  );
}
